import GameConfig from "../core/data/GameConfig";
import { GameObject } from "../core/GameObject";
import NineSlicedContainer from "../core/NineSlicedContainer";
import PlayerData from "../PlayerData";
import { GameScene } from "../scenes/GameScene";
import ActionQueue from "../utils/ActionQueue";
import { Player } from "./Player";
import CharacterAtlas from "./PlayerStates/CharacterAtlas";

export class Slingshot extends GameObject {

    gameScene: GameScene;
	pointerDown = false;
    dragStart: Phaser.Math.Vector2;
    stretchPos: Phaser.Math.Vector2;
    dragThreshold = 2;

    player: Player;
    playerImgCopy: Phaser.GameObjects.Image;
    enabled = true;

    strings: NineSlicedContainer[] = [];
    seat: Phaser.GameObjects.Image;

    static ON_RELEASE = "onRelease";

	constructor(scene: GameScene) {
		super(scene);
        this.gameScene = scene;

		const inputObj = this.addImage("whiteSquare");
		inputObj.setInteractive();
		inputObj.setSize(400, 400);
		inputObj.setDisplaySize(400, 400);
        inputObj.alpha = 0.0001;
        const string2 = new NineSlicedContainer(scene, "slingshotString", 16, 16, 2, 2, 2, 2, 0, 0.5);
        this.add(string2);
        string2.setPosition(60, -120);
        this.seat = this.addImage("slingshotSeat");
        this.playerImgCopy = this.addImage("whiteSquare");
        this.playerImgCopy.setVisible(false);
        const string1 = new NineSlicedContainer(scene, "slingshotString", 16, 16, 2, 2, 2, 2, 0, 0.5);
        this.add(string1);
        string1.setPosition(-90, -55);
        const img = this.addImage("slingshot");
        this.strings.push(string1, string2);

		inputObj.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.onPointerDown, this);
        scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove, this);
        scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp, this);
	}

    onPointerDown(ptr: Phaser.Input.Pointer): void {
        if (!this.enabled) return;
        this.dragStart = this.scene.cameras.main.getWorldPoint(ptr.x, ptr.y);
        this.pointerDown = true;
        this.scene.soundManager.play("slingshotPull");
    }

    setPlayer(player: Player): void {
        this.player = player;
        this.playerImgCopy.setTexture(player.img.texture.key, CharacterAtlas.characters[player.character.name]["Idle"]);
        this.playerImgCopy.setDisplaySize(player.img.displayWidth, player.img.displayHeight);
        this.player.img.setVisible(false);
        this.playerImgCopy.setVisible(true);
        this.playerImgCopy.setPosition(player.x + player.img.x, player.y + player.img.y);
        this.positionPlayer(new Phaser.Math.Vector2(-2, 3));
    }

    onPointerMove(ptr: Phaser.Input.Pointer): void {
        if (this.pointerDown) {
            const pos = this.scene.cameras.main.getWorldPoint(ptr.x, ptr.y);

            const dist = Phaser.Math.Distance.Between(this.dragStart.x, this.dragStart.y, pos.x, pos.y);
            if (dist <= this.dragThreshold) return;
            else if (dist > this.dragThreshold) {
                const dragPoint = this.getSlingEndPos(pos);
				this.positionPlayer(dragPoint);
            }
        }
    }

    positionPlayer(pos: Phaser.Math.Vector2): void {
        const origin = new Phaser.Math.Vector2(this.x, this.y);
        const slingOffset = this.player.character.slingSeatOffset;
        origin.x -= pos.x * 10;
        origin.y -= pos.y * 10;
        this.player.planckBody.setPosition(origin.x, origin.y);
        this.player.setAngle(Math.atan2(pos.y, pos.x) + Math.PI / 2);
        this.positionString(this.player.x - 40, this.player.y + 40);
        this.playerImgCopy.setPosition(this.player.x - this.x + this.player.img.x + slingOffset.x, this.player.y - this.y + this.player.img.y + slingOffset.y);
    }

    shootAnim(): void {
        const pos = new Phaser.Math.Vector2(this.stretchPos.x, this.stretchPos.y);
        const vel = this.player.planckBody.velocity;
        const queue = new ActionQueue();
        queue.add(complete => {
            this.scene.tweens.add({
                targets: pos,
                x: this.x + vel.x * 15,
                y: this.y + vel.y * 15,
                duration: 150,
                onUpdate: () => {
                    this.positionString(pos.x, pos.y);
                    this.playerImgCopy.setPosition(this.player.x - this.x + this.player.img.x, this.player.y - this.y + this.player.img.y);
                },
                onComplete: () => {
                    this.playerImgCopy.setVisible(false);
                    this.player.img.setVisible(true);
                    this.player.playWhoom();
                    complete();
                },
            });
        });
        queue.add(() => {
            this.scene.tweens.add({
                targets: pos,
                x: this.x,
                y: this.y,
                duration: 150,
                onUpdate: () => this.positionString(pos.x, pos.y),
            });
        });
        queue.start();
    }

    positionString(x: number, y: number) {
        // console.log("Position string: ", x, y);
        this.stretchPos = new Phaser.Math.Vector2(x, y);
        this.seat.setPosition(x - this.x, y - this.y);
        for (let i = 0; i < 2; i++) {
            let pos = new Phaser.Math.Vector2(x - 23, y + 20);
            if (i > 0) pos = new Phaser.Math.Vector2(x + 27, y -18);
            const string = this.strings[i];
            const stringX = string.x + this.x;
            const stringY = string.y + this.y;
            const ang = Math.atan2(pos.y - stringY, pos.x - stringX);
            const distance = Phaser.Math.Distance.Between(stringX, stringY, pos.x, pos.y);
            string.rotation = ang;
            string.slicedWidth = distance;
            // console.log("width", distance)
        }
    }

    onPointerUp(ptr: Phaser.Input.Pointer): void {
        if (this.pointerDown) {
            this.pointerDown = false;
			const vel = this.getVelocity(this.scene.cameras.main.getWorldPoint(ptr.x, ptr.y));
            // console.log(vel);
            const powerMultiplier = this.gameScene.ui.launchMeter.getPower();
            const newVel = new Phaser.Math.Vector2(vel.x * powerMultiplier, vel.y * powerMultiplier);
            if (vel.length() > 1) {
                this.emit(Slingshot.ON_RELEASE, newVel);
                this.gameScene.ui.tweenUI();
                this.gameScene.ltGame.playExitTweens();
                this.enabled = false;
            }
        }
    }
	
	getVelocity(pos: Phaser.Math.Vector2): Phaser.Math.Vector2 {
        let vel = pos
            .subtract(this.dragStart)
            .scale(1 / 10);

        vel.y *= 0.75;
        const max = PlayerData.getItemValue("rubberBand");
        const slingMax = GameConfig.get("slingshotMaxDistance") as number;
        const magnitude = vel.distance({ x: 0, y: 0 }) * (max / slingMax);
        const minAngle = GameConfig.get<number>("slingshotMinAngle") * Phaser.Math.DEG_TO_RAD;
        const maxAngle = GameConfig.get<number>("slingshotMaxAngle") * Phaser.Math.DEG_TO_RAD;
        let ang = Math.atan2(vel.y, vel.x);
        while (ang < 0) ang += 2 * Math.PI;
        if (ang < minAngle) ang = minAngle;
        if (ang > maxAngle) ang = maxAngle;
        vel = new Phaser.Math.Vector2(Math.cos(ang) * magnitude, Math.sin(ang) * magnitude);
        
        if (magnitude > max) {
            vel.scale(max / magnitude);
        }
        return vel.negate();
    }

    getSlingEndPos(pos: Phaser.Math.Vector2): Phaser.Math.Vector2 {
        let vel = pos
            .subtract(this.dragStart)
            .scale(1 / 10);

        vel.y *= 0.75;

        const magnitude = vel.distance({ x: 0, y: 0 });
        const minAngle = GameConfig.get<number>("slingshotMinAngle") * Phaser.Math.DEG_TO_RAD;
        const maxAngle = GameConfig.get<number>("slingshotMaxAngle") * Phaser.Math.DEG_TO_RAD;
        let ang = Math.atan2(vel.y, vel.x);
        while (ang < 0) ang += 2 * Math.PI;
        if (ang < minAngle) ang = minAngle;
        if (ang > maxAngle) ang = maxAngle;
        vel = new Phaser.Math.Vector2(Math.cos(ang) * magnitude, Math.sin(ang) * magnitude);
        
        const slingMax = GameConfig.get("slingshotMaxDistance") as number;
        if (magnitude > slingMax) {
            vel.scale(slingMax / magnitude);
        }
        return vel.negate();
    }

}